import { z } from "zod"

export const THREAD_ACTIVITY_TYPES = [
	"thread_snoozed",
	"thread_unsnoozed",
	"thread_marked_done",
	"thread_reopened",
	"thread_merged",
	"thread_split",
	"assignee_changed",
	"self_assigned",
	"contact_changed",
	"company_changed",
	"issue_linked",
	"issue_unlinked",
	"issue_created",
	"project_linked",
	"project_unlinked",
	"tag_linked",
	"tag_unlinked",
	"priority_changed",
	"status_changed",
	"linked_issue_planned",
	"linked_issue_started",
	"linked_issue_completed",
	"linked_issue_cancelled",
	"linked_project_completed",
	"linked_project_cancelled",
	"sla_started",
	"sla_paused",
	"sla_breached",
	"sla_resolved",
	"sla_policy_set",
	"sla_policy_removed",
	"pr_opened",
	"pr_merged",
	"pr_closed",
	"auto_assigned",
	"coding_agent_triggered",
	"coding_agent_running",
	"coding_agent_finished",
	"coding_agent_failed",
	"coding_agent_stopped",
	"bug_inspect_requested",
	"bug_inspect_reproduced",
	"bug_inspect_not_reproduced",
	"bug_inspect_blocked",
	"bug_inspect_pr_opened",
	"bug_inspect_approved",
	"bug_inspect_rejected",
	"docs_proposal_started",
	"docs_proposal_ready",
	"docs_proposal_skipped",
	"custom_data_action_run",
	"ai_auto_reply_started",
	"ai_auto_reply_connectors",
	"ai_reply_trace",
	"ai_handoff",
	"ai_handoff_reviewed",
	"ai_notified",
	"ai_reply_skipped",
	"ai_draft_skipped",
	"ai_chat_feedback",
	"ai_shadow_reply",
	"ai_resolution_changed",
	"routine_run",
	"routine_note",
	"close_loop_sent",
	"close_loop_skipped",
	"feature_request_card",
	"linear_asks_posted",
] as const

export type ThreadActivityType = (typeof THREAD_ACTIVITY_TYPES)[number]

export const CLOSE_LOOP_KINDS = ["reported", "upvoted"] as const

export const CLOSE_LOOP_CHANNELS = [
	"email",
	"slack",
	"teams",
	"live_chat",
	"custom",
] as const

const closeLoopSourceSchema = {
	source: z.enum(["issue", "project"]),
	sourceId: z.string(),
	identifier: z.string().optional(),
}

const closeLoopPreviewSchema = z.object({
	...closeLoopSourceSchema,
	kind: z.enum(CLOSE_LOOP_KINDS),
	channel: z.enum(CLOSE_LOOP_CHANNELS),
	trigger: z.enum(["completed", "status_changed"]),
	triggeredAt: z.string(),
	body: z.string(),
	itemName: z.string().nullable().optional(),
	howToUse: z.string().nullable().optional(),
})

export type CloseLoopPreview = z.infer<typeof closeLoopPreviewSchema>

// Absent on the dashboard's own state changes: those already carry an actor.
export const THREAD_STATE_CHANGE_SOURCES = [
	"ai_agent",
	"ai_handoff",
	"ai_notified",
	"customer_reply",
	"email_bounce",
	"unanswered_message",
	"linear_asks",
] as const

// Who put a person on the conversation: the customer pressing the handoff
// button, or the agent deciding on its own that someone has to follow up.
export const AI_HANDOFF_REASONS = [
	"customer_request",
	"agent_decision",
] as const

export type AiHandoffReason = (typeof AI_HANDOFF_REASONS)[number]

// How an agent session's resolution moved after the agent's own call: the
// end-of-conversation judge, the 24h quiet sweep, or the customer coming back.
export const AI_RESOLUTION_CHANGES = [
	"judged_resolved",
	"judged_escalated",
	"assumed_resolved",
	"revoked",
] as const

export type AiResolutionChange = (typeof AI_RESOLUTION_CHANGES)[number]

// Which AI run made a connector call, since every one of them shows as the agent.
export const CONNECTOR_RUN_SOURCES = [
	"ai_draft",
	"auto_reply",
	"preview_reply",
	"ai_assistant",
	"widget_chat",
] as const

export type ConnectorRunSource = (typeof CONNECTOR_RUN_SOURCES)[number]

export const FEATURE_REQUEST_CARD_STATUSES = [
	"pending",
	"published",
	"dismissed",
] as const
export const FEATURE_REQUEST_KINDS = ["issue", "project"] as const

export const LINEAR_ASKS_POST_KINDS = [
	"created",
	"follow_along",
	"accepted",
	"status",
	"duplicate",
	"comment",
	"other",
] as const

export const ROUTINE_RUN_STATUSES = ["running", "done", "failed"] as const
export const ROUTINE_STEP_STATUSES = ["running", "done", "failed"] as const
export const ROUTINE_NOTE_KINDS = ["translation", "summary", "note"] as const

/** The thread went to a cover because `userId` was out of office until `until` (ISO). */
const outOfOfficeHandoffSchema = z.object({
	userId: z.string(),
	userName: z.string(),
	until: z.string(),
})

export const AI_SHADOW_REPLY_CHANNELS = [
	"email",
	"slack",
	"teams",
	"support_portal",
	"api",
] as const
export const AI_SHADOW_REPLY_STATUSES = [
	"pending",
	"sent",
	// A teammate's note was saved and the agent is writing the reply again.
	"revising",
	"declined",
	"superseded",
] as const

export type AiShadowReplyChannel = (typeof AI_SHADOW_REPLY_CHANNELS)[number]
export type AiShadowReplyStatus = (typeof AI_SHADOW_REPLY_STATUSES)[number]

export type RoutineRunStatus = (typeof ROUTINE_RUN_STATUSES)[number]
export type RoutineNoteKind = (typeof ROUTINE_NOTE_KINDS)[number]

// Internal schemas — not exported. Use parseThreadActivity() instead.
const schemas = {
	thread_snoozed: z.object({ snoozedUntil: z.string() }),
	thread_unsnoozed: z.object({
		// Set when the snooze ran out; absent when a teammate unsnoozed it by hand.
		source: z.literal("snooze_ended").optional(),
	}),
	thread_marked_done: z.object({
		source: z.enum(THREAD_STATE_CHANGE_SOURCES).optional(),
		routineRunId: z.string().optional(),
		// The agent closing a conversation because the customer said it is solved,
		// or because a live chat went quiet after its answer.
		reason: z.enum(["customer_confirmed", "inactivity"]).optional(),
		// An AI chat transcript saved already done; the judge or quiet sweep reports the outcome later.
		openedDone: z.literal(true).optional(),
		// Set when a Linear Asks issue closed the thread.
		issueId: z.string().optional(),
		issueIdentifier: z.string().optional(),
		outcome: z.enum(["completed", "canceled"]).optional(),
		asksNotified: z.boolean().optional(),
	}),
	thread_reopened: z.object({
		previousState: z.string(),
		source: z.enum(THREAD_STATE_CHANGE_SOURCES).optional(),
		routineRunId: z.string().optional(),
	}),
	thread_merged: z.object({
		sourceThreadId: z.string(),
		sourceTitle: z.string().nullable(),
		targetThreadId: z.string(),
		targetTitle: z.string().nullable(),
	}),
	thread_split: z.object({
		sourceThreadId: z.string(),
		sourceTitle: z.string().nullable(),
		newThreadId: z.string(),
		newTitle: z.string(),
		messageCount: z.number().int().positive(),
	}),
	assignee_changed: z.object({
		oldAssigneeId: z.string().nullable().optional(),
		newAssigneeId: z.string().nullable().optional(),
		routineRunId: z.string().optional(),
		outOfOffice: outOfOfficeHandoffSchema.optional(),
	}),
	self_assigned: z.object({
		assigneeId: z.string(),
		oldAssigneeId: z.string().nullable().optional(),
	}),
	contact_changed: z.object({
		oldContactId: z.string().nullable().optional(),
		newContactId: z.string().nullable().optional(),
		oldContactName: z.string().nullable().optional(),
		newContactName: z.string().nullable().optional(),
		newContactImage: z.string().nullable().optional(),
	}),
	company_changed: z.object({
		oldCompanyId: z.string().nullable().optional(),
		newCompanyId: z.string().nullable().optional(),
		oldCompanyName: z.string().nullable().optional(),
		newCompanyName: z.string().nullable().optional(),
		newCompanyImage: z.string().nullable().optional(),
	}),
	issue_linked: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
		issueTitle: z.string().optional(),
		routineRunId: z.string().optional(),
		linkedVia: z.enum(["linear_asks"]).optional(),
	}),
	issue_unlinked: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
		issueTitle: z.string().optional(),
	}),
	issue_created: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
		issueTitle: z.string().optional(),
		routineRunId: z.string().optional(),
	}),
	project_linked: z.object({
		projectId: z.string(),
		projectName: z.string().optional(),
	}),
	project_unlinked: z.object({
		projectId: z.string(),
		projectName: z.string().optional(),
	}),
	tag_linked: z.object({
		tagId: z.string(),
		tagName: z.string().optional(),
		tagIcon: z.string().optional(),
		tagColor: z.string().optional(),
		routineRunId: z.string().optional(),
	}),
	tag_unlinked: z.object({
		tagId: z.string(),
		tagName: z.string().optional(),
		tagIcon: z.string().optional(),
		tagColor: z.string().optional(),
	}),
	priority_changed: z.object({
		oldPriority: z.string(),
		newPriority: z.string(),
		routineRunId: z.string().optional(),
	}),
	status_changed: z.object({
		oldStatus: z.string(),
		newStatus: z.string(),
	}),
	linked_issue_planned: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
	}),
	linked_issue_started: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
	}),
	linked_issue_completed: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
	}),
	linked_issue_cancelled: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
	}),
	linked_project_completed: z.object({
		projectId: z.string(),
		projectName: z.string().optional(),
	}),
	linked_project_cancelled: z.object({
		projectId: z.string(),
		projectName: z.string().optional(),
	}),
	sla_started: z.object({
		deadlineAt: z.string(),
		policyName: z.string().optional(),
	}),
	sla_paused: z.object({}),
	sla_breached: z.object({ deadlineAt: z.string() }),
	sla_resolved: z.object({}),
	sla_policy_set: z.object({
		deadlineAt: z.string(),
		durationMinutes: z.number(),
		policyName: z.string().optional(),
	}),
	sla_policy_removed: z.object({}),
	pr_opened: z.object({
		url: z.string(),
		title: z.string(),
		sourceBranch: z.string().optional(),
		number: z.number().optional(),
		issueId: z.string().optional(),
		issueIdentifier: z.string().optional(),
	}),
	pr_merged: z.object({
		url: z.string(),
		title: z.string(),
		sourceBranch: z.string().optional(),
		number: z.number().optional(),
		issueId: z.string().optional(),
		issueIdentifier: z.string().optional(),
	}),
	pr_closed: z.object({
		url: z.string(),
		title: z.string(),
		sourceBranch: z.string().optional(),
		number: z.number().optional(),
		issueId: z.string().optional(),
		issueIdentifier: z.string().optional(),
	}),
	auto_assigned: z.object({
		assigneeId: z.string(),
		// the reason is being mapped to text in the frontend
		reason: z.enum(["linear_customer_owner", "close_loop_owner"]),
		outOfOffice: outOfOfficeHandoffSchema.optional(),
	}),
	coding_agent_triggered: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
		issueTitle: z.string().optional(),
		triggerSource: z.string(),
		provider: z.string().optional(),
	}),
	coding_agent_running: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
		issueTitle: z.string().optional(),
		triggerSource: z.string(),
		provider: z.string().optional(),
	}),
	coding_agent_finished: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
		issueTitle: z.string().optional(),
		triggerSource: z.string(),
		prUrl: z.string().optional(),
		provider: z.string().optional(),
	}),
	coding_agent_failed: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
		issueTitle: z.string().optional(),
		triggerSource: z.string(),
		errorMessage: z.string().optional(),
		provider: z.string().optional(),
	}),
	coding_agent_stopped: z.object({
		issueId: z.string(),
		issueIdentifier: z.string().optional(),
		issueTitle: z.string().optional(),
		triggerSource: z.string(),
		provider: z.string().optional(),
	}),
	bug_inspect_requested: z.object({
		runId: z.string(),
		contactId: z.string().optional(),
	}),
	bug_inspect_reproduced: z.object({
		runId: z.string(),
		issueIdentifier: z.string().optional(),
	}),
	bug_inspect_not_reproduced: z.object({
		runId: z.string(),
		verdict: z.enum(["NOT_REPRODUCED", "NEEDS_MORE_INFO", "INCONCLUSIVE"]),
	}),
	bug_inspect_blocked: z.object({
		runId: z.string().optional(),
		reason: z.string(),
	}),
	bug_inspect_pr_opened: z.object({
		runId: z.string(),
		prNumber: z.number(),
	}),
	bug_inspect_approved: z.object({
		runId: z.string(),
		prNumber: z.number(),
	}),
	bug_inspect_rejected: z.object({
		runId: z.string(),
		prNumber: z.number(),
	}),
	docs_proposal_started: z.object({
		proposalGroupId: z.string(),
		issueId: z.string().optional(),
		issueIdentifier: z.string().optional(),
	}),
	docs_proposal_ready: z.object({
		proposalGroupId: z.string(),
		groupTitle: z.string().optional(),
		childCount: z.number(),
		// Deep link into the review rail. Absent on rows written before it existed,
		// which fall back to the docs home.
		reviewPath: z.string().optional(),
		issueId: z.string().optional(),
		issueIdentifier: z.string().optional(),
	}),
	docs_proposal_skipped: z.object({
		proposalGroupId: z.string(),
		reason: z.string().optional(),
	}),
	// Live for as long as the agent is writing. Soft-deleted the moment the
	// reply lands or the run gives up, so the timeline shows it as a spinner
	// and nothing is left behind once the answer is there.
	ai_auto_reply_started: z.object({
		channel: z.enum(["email", "slack", "teams"]),
	}),
	// Attribution for an unattended auto-reply. Rendered on the sent email
	// rather than as its own timeline row. Mirrors ConnectorAttribution, which
	// lives in zod v4 and cannot be composed into this v3 map.
	ai_auto_reply_connectors: z.object({
		emailId: z.string(),
		connectors: z.array(
			z.object({
				sectionId: z.string(),
				title: z.string(),
				tool: z.string(),
				kind: z.enum(["http", "mcp", "builtin"]),
				ok: z.boolean(),
			}),
		),
	}),
	// What the agent looked up for one sent reply, keyed by that message's row.
	// Never rendered as its own row; Improve reply reads it, and on Slack and
	// Teams it is the only sign that a bot message carries an agent answer.
	ai_reply_trace: z.object({
		channel: z.enum(["email", "slack", "teams"]),
		messageId: z.string(),
		trace: z.array(
			z.object({
				tool: z.string(),
				input: z.string().optional(),
				ok: z.boolean(),
				error: z.string().optional(),
				results: z.array(z.string()).optional(),
			}),
		),
		// The bot posted only the out-of-office notice: the agent's row, but no answer.
		outOfOfficeOnly: z.boolean().optional(),
	}),
	custom_data_action_run: z.object({
		sectionId: z.string(),
		sectionTitle: z.string(),
		action: z.string(),
		/** Which guard variant of the tool ran, when the tool has more than one. */
		variant: z.string().optional(),
		actor: z.enum(["teammate", "agent"]),
		// Absent on rows written before the source was recorded.
		source: z.enum(CONNECTOR_RUN_SOURCES).optional(),
		success: z.boolean(),
		input: z.record(z.unknown()).optional(),
		message: z.string().optional(),
		guarded: z.boolean().optional(),
		injected: z.array(z.string()).optional(),
		/** Paths whose agent-supplied value a rule rewrote rather than replaced. */
		transformed: z.array(z.string()).optional(),
		fallbackSteps: z
			.array(z.object({ path: z.string(), step: z.number(), of: z.number() }))
			.optional(),
		policyViolation: z.string().optional(),
	}),
	ai_handoff: z.object({
		sessionId: z.string(),
		// Absent on rows written before the reason was recorded, every one of
		// which was the customer pressing the handoff button.
		reason: z.enum(AI_HANDOFF_REASONS).optional(),
		// What the agent's tools did in the turn it handed off. Internal only.
		agentTrace: z
			.array(
				z.object({
					tool: z.string(),
					input: z.string().optional(),
					ok: z.boolean(),
					error: z.string().optional(),
					results: z.array(z.string()).optional(),
				}),
			)
			.optional(),
	}),
	ai_handoff_reviewed: z.object({}),
	// The agent filed or linked an issue and told the customer the team has it.
	// The conversation counts as resolved; the thread waits in the inbox to be seen.
	ai_notified: z.object({ sessionId: z.string() }),
	// The agent read the message and sent nothing. "silent" means it named no
	// ground; the list is spelled out because AI_SKIP_REPLY_GROUNDS is zod v4.
	ai_reply_skipped: z.object({
		ground: z.enum(["automated", "bulk", "no-help", "silent"]),
		detail: z.string().optional(),
		channel: z.enum(["email", "slack", "teams"]),
	}),
	// The model narrated its own reasoning instead of writing a reply, so the
	// composer stays empty and the timeline carries what happened instead.
	ai_draft_skipped: z.object({
		reason: z.enum(["meta-narrative", "instruction-conflict"]),
		summary: z.string(),
	}),
	ai_chat_feedback: z.object({
		sentiment: z.enum(["positive", "negative"]),
		message: z.string().optional(),
	}),
	ai_resolution_changed: z.object({
		sessionId: z.string(),
		change: z.enum(AI_RESOLUTION_CHANGES),
		reason: z.string().optional(),
	}),
	// What the agent would have answered on a channel it is not deployed to.
	// Teammate only: never sent, billed, or exposed on a public surface.
	ai_shadow_reply: z.object({
		html: z.string(),
		channel: z.enum(AI_SHADOW_REPLY_CHANNELS),
		status: z.enum(AI_SHADOW_REPLY_STATUSES),
		sources: z
			.array(
				z.object({
					type: z.string(),
					title: z.string(),
					url: z.string().nullable().optional(),
				}),
			)
			.optional(),
		inboundMessageId: z.string().nullable().optional(),
		decidedAt: z.string().nullable().optional(),
		decidedById: z.string().nullable().optional(),
		revisionNote: z.string().optional(),
		// What the agent wrote when a teammate edited the preview before sending; `html` is what went out.
		originalHtml: z.string().optional(),
		// What the agent took from the teammate's edit or note. `undone` once they took the rule back out.
		lesson: z
			.object({
				target: z.enum(["instructions", "docs", "none"]),
				rule: z.string().optional(),
				undone: z.boolean().optional(),
			})
			.optional(),
		// A close-loop follow-up auto-send would have sent. Send replays it through
		// the close-loop run, so the stored phrases keep the text as approved.
		closeLoop: closeLoopPreviewSchema.optional(),
	}),
	// Rewritten in place as the run progresses, so the thread view animates off
	// the Zero sync of this one row rather than a separate transport.
	routine_run: z.object({
		runId: z.string(),
		status: z.enum(ROUTINE_RUN_STATUSES),
		steps: z.array(
			z.object({
				label: z.string(),
				status: z.enum(ROUTINE_STEP_STATUSES),
			}),
		),
		actions: z.array(z.string()).default([]),
		// Why a write the routine attempted did not happen, in the words the tool
		// refused with.
		skipped: z.array(z.string()).default([]),
		error: z.string().optional(),
	}),
	routine_note: z.object({
		runId: z.string(),
		kind: z.enum(ROUTINE_NOTE_KINDS),
		title: z.string().optional(),
		body: z.string(),
		sourceLanguage: z.string().optional(),
	}),
	close_loop_sent: z.object({
		...closeLoopSourceSchema,
		kind: z.enum(CLOSE_LOOP_KINDS),
		channel: z.enum(CLOSE_LOOP_CHANNELS),
		recipient: z.string().optional(),
		body: z.string(),
		// Absent while the send is in flight; the claim that stops a second follow-up.
		messageId: z.string().optional(),
	}),
	close_loop_skipped: z.object({
		...closeLoopSourceSchema,
		reason: z.string(),
		detail: z.string().optional(),
	}),
	// Team only: offers to publish a portal request on the portal it came from.
	// `title` and `description` come from the customer's own words, so they are
	// safe to use as the item's public copy.
	feature_request_card: z.object({
		status: z.enum(FEATURE_REQUEST_CARD_STATUSES),
		portalId: z.string(),
		kind: z.enum(FEATURE_REQUEST_KINDS),
		title: z.string(),
		description: z.string(),
		match: z
			.object({ type: z.enum(FEATURE_REQUEST_KINDS), id: z.string() })
			.nullable(),
		publishedItem: z
			.object({ type: z.enum(FEATURE_REQUEST_KINDS), id: z.string() })
			.nullable()
			.optional(),
		decidedAt: z.string().nullable().optional(),
		decidedById: z.string().nullable().optional(),
	}),
	// Never a message, so never our reply. `text` only on synced comments: the
	// other posts repeat the Linear issue title.
	linear_asks_posted: z.object({
		kind: z.enum(LINEAR_ASKS_POST_KINDS),
		slackTs: z.string(),
		issueIdentifier: z.string().optional(),
		duplicateOf: z.string().optional(),
		status: z.string().optional(),
		author: z.string().optional(),
		text: z.string().optional(),
	}),
} as const satisfies Record<ThreadActivityType, z.ZodTypeAny>

/** Maps each event type to its validated payload type */
export type ThreadActivityPayloadMap = {
	[K in ThreadActivityType]: z.infer<(typeof schemas)[K]>
}

/** Discriminated union of all parsed thread activities */
export type ParsedThreadActivity = {
	[K in ThreadActivityType]: {
		eventType: K
		payload: ThreadActivityPayloadMap[K]
	}
}[ThreadActivityType]

/**
 * The single entry point for parsing thread activities.
 * Takes a raw eventType string + unknown payload, validates against the correct schema,
 * and returns a fully typed discriminated union object.
 *
 * @throws Error if eventType is unknown or payload doesn't match schema
 */
export function parseThreadActivity(
	eventType: string,
	payload: unknown,
): ParsedThreadActivity {
	const schema = schemas[eventType as ThreadActivityType]
	if (!schema) {
		throw new Error(`Unknown thread activity type: ${eventType}`)
	}
	return {
		eventType,
		payload: schema.parse(payload),
	} as ParsedThreadActivity
}
